import {
  useAtticInventoryReportCsvLazyQuery,
  useAtticInventoryReportQuery,
} from "@dashboard/graphql";
import { useNotifier } from "@dashboard/hooks/useNotifier/useNotifier";
import { useIntl } from "react-intl";

import AtticReportPage from "../components/AtticReportPage/AtticReportPage";
import { sectionMessages } from "../messages";

/** `inventory-2026-10-30T14-05.csv`: the report's timestamp, filename-safe. */
export const csvFileName = (generatedAt: string) =>
  `inventory-${generatedAt.slice(0, 16).replace(":", "-")}.csv`;

/** Hands the browser a CSV to save, through a transient object URL. */
export const saveCsv = (text: string, name: string) => {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const AtticReportView = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const { data, loading, error } = useAtticInventoryReportQuery({
    displayLoader: true,
    fetchPolicy: "network-only",
  });
  // The CSV can be large, so it is not part of the page's query: fetched on
  // the button, straight to a file.
  const [fetchCsv, { loading: downloading }] = useAtticInventoryReportCsvLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: result => {
      const report = result.atticInventoryReport;

      if (report) {
        saveCsv(report.csv, csvFileName(report.generatedAt));
      }
    },
    onError: () =>
      notify({ status: "error", text: intl.formatMessage(sectionMessages.loadFailed) }),
  });

  return (
    <AtticReportPage
      report={loading && !data ? undefined : (data?.atticInventoryReport ?? undefined)}
      loadFailed={!data && !loading && !!error}
      downloading={downloading}
      onDownloadCsv={() => fetchCsv()}
    />
  );
};

export default AtticReportView;
